import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import inventoryApi from '../../../services/inventoryApi';
import { useNotifications } from '../../../hooks/useNotifications';
import '../ProductManagement/ProductForm.css';
import './styles.css';

const Dashboard = ({ organization = 'TestOrg' }) => {
  const { t } = useTranslation();
  const { pushToast } = useNotifications();
  const [kpis, setKpis] = useState({
    totalProducts: 0,
    lowStockItems: 0,
    pendingPOs: 0,
    openSOs: 0,
    pendingReturns: 0,
    totalStockValue: 0
  });

  const [recentMovements, setRecentMovements] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [degraded, setDegraded] = useState(false);
  const [failedCount, setFailedCount] = useState(0);
  const totalWidgets = 7;

  useEffect(() => {
    loadDashboardData();
  }, [organization]);

  const loadDashboardData = async () => {
    setLoading(true);
    setDegraded(false);
    setFailedCount(0);

    const results = await Promise.allSettled([
      inventoryApi.getProducts(organization),
      inventoryApi.getStockAlerts(organization, { status: 'active' }),
      inventoryApi.getPurchaseOrders(organization, 'pending'),
      inventoryApi.getSalesOrders(organization, null, 'confirmed'),
      inventoryApi.getReturnOrders(organization, null, 'pending'),
      inventoryApi.getStockMovements(organization),
      inventoryApi.getInventoryValuations(organization),
    ]);

    let failures = 0;
    const extract = (result) => {
      if (result.status === 'rejected') {
        failures++;
        return [];
      }
      const val = result.value;
      return Array.isArray(val) ? val : (val?.data || []);
    };

    const productsData = extract(results[0]);
    const alertsData = extract(results[1]);
    const posData = extract(results[2]);
    const sosData = extract(results[3]);
    const returnsData = extract(results[4]);
    const movementsData = extract(results[5]);
    const valuationsData = extract(results[6]);

    if (failures > 0) {
      setDegraded(true);
      setFailedCount(failures);
      if (pushToast) {
        pushToast({
          type: 'api-error',
          title: 'Dashboard partially loaded',
          body: `${failures} of ${totalWidgets} widgets failed. Showing available data.`,
        });
      }
    }

    setKpis({
      totalProducts: productsData.length,
      lowStockItems: alertsData.filter(a => (a.alert_type || a.alertType) === 'low_stock').length,
      pendingPOs: posData.length,
      openSOs: sosData.length,
      pendingReturns: returnsData.length,
      totalStockValue: valuationsData.length > 0
        ? valuationsData.reduce((sum, v) => sum + (v.total_value || v.totalValue || 0), 0)
        : 0
    });

    setRecentMovements(movementsData.slice(0, 10));
    setAlerts(alertsData);
    setLoading(false);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="pm-page-card pm-skeleton" aria-busy="true">
        {t('inventory.loading_dashboard')}
      </div>
    );
  }

  return (
    <div className="pm-page-card">
      {degraded && (
        <div className="pm-error-banner" role="alert">
          <span aria-hidden="true" style={{ fontSize: '1.25rem' }}>⚠️</span>
          <div style={{ flex: 1 }}>
            <strong>{t('inventory.data_partially_unavailable')}</strong>
            <span style={{ color: 'var(--pico-muted-color)', marginLeft: 8 }}>
              ({failedCount} {t('inventory.of')} {totalWidgets} {t('inventory.widgets_failed')})
            </span>
          </div>
          <button onClick={loadDashboardData} className="pm-btn-secondary">
            {t('inventory.retry_all')}
          </button>
        </div>
      )}

      <div className="pm-header">
        <h2 className="pm-header-title">{t('inventory.dashboard_title')}</h2>
        <div className="pm-header-actions">
          <button onClick={loadDashboardData} className="pm-btn-secondary">
            <span aria-hidden="true">🔄</span>
            <span className="desktop-only">{t('inventory.refresh_button')}</span>
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="pm-kpi-grid">
        <div className="pm-kpi-card">
          <div className="pm-kpi-icon" aria-hidden="true">📦</div>
          <div className="pm-kpi-content">
            <div className="pm-kpi-value">{kpis.totalProducts}</div>
            <div className="pm-kpi-label">{t('inventory.total_products')}</div>
          </div>
        </div>

        <div className="pm-kpi-card warning">
          <div className="pm-kpi-icon" aria-hidden="true">⚠️</div>
          <div className="pm-kpi-content">
            <div className="pm-kpi-value">{kpis.lowStockItems}</div>
            <div className="pm-kpi-label">{t('inventory.low_stock_alerts')}</div>
          </div>
        </div>

        <div className="pm-kpi-card">
          <div className="pm-kpi-icon" aria-hidden="true">📥</div>
          <div className="pm-kpi-content">
            <div className="pm-kpi-value">{kpis.pendingPOs}</div>
            <div className="pm-kpi-label">{t('inventory.pending_purchase_orders')}</div>
          </div>
        </div>

        <div className="pm-kpi-card">
          <div className="pm-kpi-icon" aria-hidden="true">📤</div>
          <div className="pm-kpi-content">
            <div className="pm-kpi-value">{kpis.openSOs}</div>
            <div className="pm-kpi-label">{t('inventory.open_sales_orders')}</div>
          </div>
        </div>

        <div className="pm-kpi-card">
          <div className="pm-kpi-icon" aria-hidden="true">↩️</div>
          <div className="pm-kpi-content">
            <div className="pm-kpi-value">{kpis.pendingReturns}</div>
            <div className="pm-kpi-label">{t('inventory.pending_returns')}</div>
          </div>
        </div>

        <div className="pm-kpi-card success">
          <div className="pm-kpi-icon" aria-hidden="true">💰</div>
          <div className="pm-kpi-content">
            <div className="pm-kpi-value">{formatCurrency(kpis.totalStockValue)}</div>
            <div className="pm-kpi-label">{t('inventory.total_stock_value')}</div>
          </div>
        </div>
      </div>

      <div className="pm-dashboard-grid">
        {/* Alerts Panel */}
        <div className="pm-dashboard-section">
          <h3>{t('inventory.active_alerts')}</h3>
          {alerts.length === 0 ? (
            <div className="pm-empty-state">
              <div className="pm-empty-state-icon" aria-hidden="true">🚨</div>
              <p>{t('inventory.no_active_alerts')}</p>
            </div>
          ) : (
            <div className="pm-alerts-list">
              {alerts.slice(0, 5).map((alert, index) => {
                const alertType = alert.alert_type || alert.alertType || 'unknown';
                const currentQty = alert.current_quantity || alert.currentQuantity || 0;
                const thresholdQty = alert.threshold_quantity || alert.thresholdQuantity || 0;

                return (
                  <div key={alert.id || index} className={`pm-alert-item ${alert.severity || 'low'}`}>
                    <div className="pm-alert-header">
                      <span className="pm-alert-type">{alertType.replace('_', ' ').toUpperCase()}</span>
                      <span className="pm-alert-severity">{alert.severity}</span>
                    </div>
                    <div className="pm-alert-message">{alert.message}</div>
                    <div className="pm-alert-details">
                      {t('inventory.current')} {currentQty} | {t('inventory.threshold')} {thresholdQty}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Recent Stock Movements */}
        <div className="pm-dashboard-section">
          <h3>{t('inventory.recent_stock_movements')}</h3>
          {recentMovements.length === 0 ? (
            <div className="pm-empty-state">
              <div className="pm-empty-state-icon" aria-hidden="true">📋</div>
              <p>{t('inventory.no_recent_movements')}</p>
            </div>
          ) : (
            <div className="pm-table-wrap">
              <table className="pm-table" role="grid">
                <thead>
                  <tr>
                    <th>{t('inventory.date')}</th>
                    <th>{t('inventory.type')}</th>
                    <th>{t('inventory.product_id')}</th>
                    <th>{t('inventory.quantity')}</th>
                    <th>{t('inventory.warehouse')}</th>
                  </tr>
                </thead>
                <tbody>
                  {recentMovements.map((movement, index) => {
                    const movementDate = movement.movement_date || movement.movementDate || movement.created_at;
                    const movementType = movement.movement_type || movement.movementType || 'unknown';
                    const productId = movement.product_id || movement.productId;
                    const warehouseId = movement.warehouse_id || movement.warehouseId;

                    return (
                      <tr key={movement.id || index}>
                        <td>{formatDate(movementDate)}</td>
                        <td>
                          <span className={`pm-movement-badge ${movementType}`}>
                            {movementType}
                          </span>
                        </td>
                        <td>{productId}</td>
                        <td className={movement.quantity > 0 ? 'pm-quantity-positive' : 'pm-quantity-negative'}>
                          {movement.quantity > 0 ? '+' : ''}{movement.quantity}
                        </td>
                        <td>{warehouseId}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="pm-quick-actions">
        <h3>{t('inventory.quick_actions')}</h3>
        <div className="pm-quick-actions-grid">
          <button className="pm-btn-primary">
            <span aria-hidden="true">📦</span>
            {t('inventory.create_product')}
          </button>
          <button className="pm-btn-secondary">
            <span aria-hidden="true">📥</span>
            {t('inventory.new_purchase_order')}
          </button>
          <button className="pm-btn-secondary">
            <span aria-hidden="true">📤</span>
            {t('inventory.new_sales_order')}
          </button>
          <button className="pm-btn-secondary">
            <span aria-hidden="true">🔧</span>
            {t('inventory.adjust_stock')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
